import RestService from '@onpace/api_service/rest_service'

/// Indicates the amount of time to wait after scheduling a collect.
const COLLECT_WAIT_PERIOD = 60_000

/// Reports analytics events to Onspace.
///
/// Do not create new instances of this element, instead use the globally exported instance from this file. Before this
/// can be used, it must be configured using OnspaceAnalyticsReporter.configure.
///
/// This tracks page views and events locally, collecting them to send in a combined request. This greatly reduces the
/// load by reducing the number of requests sent to the server. To force a send of the current data, use the
/// +performCollect+ function.
class OnspaceAnalyticsReporter {
  /// Configures the reporter.
  ///
  /// This must be called before using any of the other features of the reporter. If this is not the case, reporting
  /// will be ignored.
  ///
  /// The following options are available:
  /// [baseUrl]
  ///   The base URL which hosts the Onspace analytics API. This should correspond to the Rails application's
  ///   +analytics_routes_prefix+, with the full domain name if required.
  ///
  ///   By default this is set to +/onspace/analytics+.
  /// [requestedWith]
  ///   Sets the +X-Requested-With+ header for all requests.
  ///
  ///   This can be used to customise the browser name and version that is stored in processed analytics when used in
  ///   combination with the +analytics_parse_requested_with+ configuration.
  ///
  ///   By default this is not set, but a value may still be sent depending on the browser.
  /// [clientHeaders]
  ///   An object of additional headers to add to each request.
  ///
  ///   This can be used to provide additional information required by the API, such as authentication.
  /// [session]
  ///   Additional custom parameters to be stored in the session.
  configure({ baseUrl = '/onspace/analytics', requestedWith, clientHeaders, session } = {}) {
    this.sessionParameters = {}
    if (typeof window.sessionStorage !== 'undefined') {
      const sessionString = sessionStorage.getItem('onspace-analytics-session-data')
      if (typeof sessionString === 'string' && sessionString.length > 0) {
        this.sessionParameters = JSON.parse(sessionString)
      }
    }

    const clientOptions = {
      baseUrl,
      defaultHeaders: clientHeaders || {}
    }

    if (requestedWith) {
      clientOptions.defaultHeaders['X-Requested-With'] = requestedWith
    }

    this.sessionUpdated = false
    this.sessionEnded = false
    this.reportedPageViews = []
    this.reportedEvents = []
    this.client = new RestService(clientOptions)

    this.updateSession(session)
  }

  /// Configures the reporter in Turbo mode.
  ///
  /// This will pass the given options to OnspaceAnalyticsReporter.configure, and setup event handlers to automatically
  /// log page views.
  configureTurbo(options = {}) {
    this.configure(options)

    window.addEventListener('beforeunload', (_event) => {
      this.endSession()
    })
    document.addEventListener('turbo:load', (event) => {
      this.reportPageView({ url: event.detail.url })
    })
  }

  ////////// Session

  /// Configure custom parameters to be stored in the session.
  ///
  /// This accepts an object of arbitrary parameters, although they need to be configured in the API. Additionally, this
  /// function also handles the screen size. The screen dimensions can be set from the +width+ and +height+ parameters,
  /// which if not given, will be set automatically.
  ///
  /// Any parameters configured here will be stored in the browser's session storage, meaning for the duration of a
  /// user's session, the parameters do not need to be continually set. When passing changes, the new parameters will be
  /// merged with the existing set, so attributes must be explicity set to +null+ to be removed.
  ///
  /// If the parameters are changed as compared to the previous parameters, the session will be marked as changed and a
  /// collect will be scheduled. Note that there will only ever be 1 set of parameters per session. If multiple data
  /// sets are required during a session, events should be used instead.
  updateSession(parameters = {}) {
    const existingParameterString = JSON.stringify(this.sessionParameters)
    this.sessionParameters = { ...this.sessionParameters, ...parameters }

    if (!this.sessionParameters.width || !this.sessionParameters.height) {
      this.sessionParameters.width = window.screen.width
      this.sessionParameters.height = window.screen.height
    }

    this.sessionParameters = Object.keys(this.sessionParameters)
      .sort()
      .reduce((obj, key) => {
        obj[key] = this.sessionParameters[key]
        return obj
      }, {})
    const sessionString = JSON.stringify(this.sessionParameters)

    if (existingParameterString != sessionString) {
      if (typeof window.sessionStorage !== 'undefined') {
        sessionStorage.setItem('onspace-analytics-session-data', sessionString)
      }
      this.sessionUpdated = true
      this.scheduleCollect()
    }
  }

  /// Reports an end session event.
  ///
  /// This marks the session as ended, and causes a collect to be performed immediately.
  endSession() {
    if (this.sessionEnded) { return }

    this.sessionEnded = true
    this.performCollect()
  }

  /// Restarts a session that had previously ended.
  ///
  /// If a session had previously ended, this will send a page view replicating the one last sent. If a session is
  /// in progress, or one had not been ended, this will do nothing.
  restartSession() {
    if (!this.sessionEnded || !this.lastPageView) { return }

    this.reportPage(this.lastPageView)
  }

  ////////// Reporting

  ///// Page View

  /// Legacy, use +reportPageView+.
  reportPage(params = {}) { this.reportPageView(params) }

  /// Reports a page view.
  ///
  /// The reported page view is added to the temporary list of page views, and a collect is scheduled. This accepts the
  /// following parameters:
  /// [:url]
  ///   The full URL of the page to report. If not explicitly provided, this is taken from the browser's current
  ///   location.
  /// [:route]
  ///   The parameterised route for the url. The reporter will attempt to infer this from the following:
  ///   - From the meta tag +onspace-analytics-route+.
  ///   - Otherwise it will be set to the +url+.
  reportPageView({ url, route } = {}) {
    this.lastPageView = { url, route }
    this.sessionEnded = false

    if (!url) {
      url = window.location.href
    }

    if (!route) {
      const routeMeta = document.querySelector('meta[name=onspace-analytics-route]')
      if (routeMeta) { route = routeMeta.content }

      if (!route) {
        const parsedUrl = new URL(url)
        route = parsedUrl.pathname
      }
    }

    const createdAt = new Date()
    this.reportedPageViews.push({ url, route, created_at: createdAt.toISOString() })
    this.scheduleCollect()
  }

  ///// Event

  /// Reports an event.
  ///
  /// The reported event is added to the temporary list of events, and a collect is scheduled. This accepts the
  /// following parameters:
  /// [eventType]
  ///   The key representing the type of event.
  /// [event]
  ///   An object with data specific to the event type.
  reportEvent(eventType, event) {
    this.sessionEnded = false

    const createdAt = new Date()
    this.reportedEvents.push({ _type: eventType, ...event, created_at: createdAt.toISOString() })
    this.scheduleCollect()
  }

  ////////// Collection

  /// Schedules a collect call to be run after a wait period.
  ///
  /// If a collect is already scheduled, this does nothing.
  scheduleCollect() {
    if (this.collectTimer) { return }

    this.collectTimer = window.setTimeout(this.performCollect.bind(this), COLLECT_WAIT_PERIOD)
  }

  /// Performs a call to the collect endpoint.
  ///
  /// In most cases, this should not be called directly, instead use +scheduleCollect+ so that it can group multiple
  /// items into a single request. Any scheduled collects will be cleared if this is called directly.
  ///
  /// This will send the following information, which is cleared afterwards:
  /// - The current session params.
  /// - Indicators if the session has been changed or updated.
  /// - Reported page views.
  /// - Reported events.
  async performCollect() {
    if (this.collectTimer) { window.clearTimeout(this.collectTimer) }
    this.collectTimer = null

    const params = {
      session: this.sessionParameters,
      page_views: this.reportedPageViews,
      events: this.reportedEvents
    }
    if (this.sessionUpdated) { params.session_changed = true }
    if (this.sessionEnded) { params.session_ended = true }

    if (!this.client) {
      console.error('[onspace-analytics] Unable to send data as the reported has not been configured') // eslint-disable-line no-console
    } else {
      await this.client.POST('/tracking/collect', { params })
    }

    this.sessionUpdated = false
    this.reportedPageViews = []
    this.reportedEvents = []
  }
}

const reporter = new OnspaceAnalyticsReporter()
export default reporter

window.onspaceAnalyticsReporter = reporter
